import { useEffect, useRef, useState } from 'react';
import { 
  connectWebSocket, 
  disconnectWebSocket, 
  authenticateWebSocket,
  joinMagicEditorRoom, 
  listenForMagicEditorUpdates, 
  removeMagicEditorListeners,
  getSocket,
  isConnected
} from '../services/websocketService';

export const useWebSocket = (userId, token) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [error, setError] = useState(null);
  const listenersRef = useRef(new Map());

  useEffect(() => {
    if (!userId || !token) return;

    // Connect to WebSocket
    const socket = connectWebSocket(token);
    authenticateWebSocket(userId, token);

    // Set up connection status listener
    const handleConnectionStatus = (data) => {
      setIsConnected(data.connected);
      setConnectionStatus(data.connected ? 'connected' : 'disconnected');
      if (!data.connected) {
        setError(data.reason || 'Connection lost');
      } else {
        setError(null);
      }
    };

    // Set up authentication listener
    const handleAuthenticated = (data) => {
      if (data.success) {
        setConnectionStatus('authenticated');
        setError(null);
      } else {
        setError('Authentication failed');
      }
    };

    // Set up authentication error listener
    const handleAuthError = (error) => {
      setError(error.message || 'Authentication error');
      setConnectionStatus('error');
    };

    // Set up connection error listener
    const handleConnectionError = (error) => {
      setError(error.message || 'Connection error');
      setConnectionStatus('error');
    };

    // Register listeners
    if (socket) {
      socket.on('connection_status', handleConnectionStatus);
      socket.on('authenticated', handleAuthenticated);
      socket.on('authentication_error', handleAuthError);
      socket.on('connection_error', handleConnectionError);
    }

    // Store listeners for cleanup
    listenersRef.current.set('connection_status', handleConnectionStatus);
    listenersRef.current.set('authenticated', handleAuthenticated);
    listenersRef.current.set('authentication_error', handleAuthError);
    listenersRef.current.set('connection_error', handleConnectionError);

    // Cleanup on unmount
    return () => {
      if (socket) {
        listenersRef.current.forEach((listener, event) => {
          socket.off(event, listener);
        });
      }
      listenersRef.current.clear();
    };
  }, [userId, token]);

  return {
    isConnected,
    connectionStatus,
    error
  };
};

export const useMagicEditorWebSocket = (soapNoteId, userId) => {
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const listenersRef = useRef(new Map());

  useEffect(() => {
    if (!soapNoteId || !userId) return;

    // Join Magic Editor session
    const token = localStorage.getItem('token') || 'test-token';
    const socket = connectWebSocket(token);
    authenticateWebSocket(userId, token);
    joinMagicEditorRoom(soapNoteId, userId);

    // Set up Magic Editor status listener
    const handleMagicEditorStatus = (data) => {
      setStatus(data.status);
      setMessage(data.message || '');

      switch (data.status) {
        case 'started':
          setIsProcessing(true);
          setProgress(0);
          setError(null);
          setResult(null);
          break;
        case 'progress':
          setProgress(data.progress || 0);
          break;
        case 'complete':
          setIsProcessing(false);
          setProgress(100);
          setResult(data.result);
          setError(null);
          break;
        case 'error':
          setIsProcessing(false);
          setError(data.error);
          setProgress(0);
          break;
        default:
          break;
      }
    };

    // Set up joined session listener
    const handleJoinedSession = (data) => {
      if (data.success) {
        console.log('Successfully joined Magic Editor session');
      }
    };

    // Set up join error listener
    const handleJoinError = (error) => {
      setError(error.message || 'Failed to join session');
    };

    // Register listeners using our WebSocket service
    listenForMagicEditorUpdates((data) => {
      switch (data.type) {
        case 'status':
          handleMagicEditorStatus(data);
          break;
        case 'complete':
          handleMagicEditorStatus({ status: 'complete', result: data.result });
          break;
        case 'error':
          handleMagicEditorStatus({ status: 'error', error: data.error });
          break;
        default:
          break;
      }
    });

    // Store listeners for cleanup
    listenersRef.current.set('magic_editor_status', handleMagicEditorStatus);
    listenersRef.current.set('joined_magic_editor', handleJoinedSession);
    listenersRef.current.set('join_error', handleJoinError);

    // Cleanup on unmount
    return () => {
      removeMagicEditorListeners();
      listenersRef.current.clear();
    };
  }, [soapNoteId, userId]);

  const reset = () => {
    setStatus(null);
    setProgress(0);
    setMessage('');
    setIsProcessing(false);
    setResult(null);
    setError(null);
  };

  return {
    status,
    progress,
    message,
    isProcessing,
    result,
    error,
    reset
  };
};

export default useWebSocket;
